import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  Box,
  Divider,
  IconButton,
  Link as MuiLink,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Pencil } from 'lucide-react';

import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { PublishedStatus } from '@modules/_shared/components/PublishedChip';
import AgentPhoneEditModal from '@modules/_shared/components/visibility-drawer/edit-modals/AgentPhoneEditModal';
import UserFieldEditModal from '@modules/_shared/components/visibility-drawer/edit-modals/EntityFieldEditModal';
import PublishMenu from '@modules/_shared/components/visibility-drawer/PublishMenu';
import BannerSection from '@modules/_shared/components/visibility-drawer/sections/BannerSection';
import {
  type DrawerConfig,
  type DrawerContextData,
  type DrawerSectionGroup,
  type EditModalType,
  type VisibilityEntity,
  isAgentEntity,
} from '@modules/_shared/components/visibility-drawer/types';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getAgentLiveProfileUrl } from '@modules/_shared/utils/agentUrls';
import { useLocale } from '@shared/hooks/locale';

const DrawerContext = createContext<DrawerContextData>({
  entity: null,
  setOpenModal: () => {},
});

export const useDrawerContext = () => useContext(DrawerContext);

const SectionGroupCard = ({
  group,
  entity,
  contextId,
  setOpenModal,
}: {
  group: DrawerSectionGroup;
  entity: VisibilityEntity;
  contextId?: string;
  setOpenModal: (modalType: EditModalType) => void;
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '10px',
        border: '1px solid',
        borderColor: 'grey.200',
        overflow: 'hidden',
      }}
    >
      <Stack divider={<Divider />}>
        {group.sections.map(section => {
          const SectionComponent = section.component;

          return (
            <Box key={String(section.id)} sx={{ p: 2 }}>
              <SectionComponent
                {...section.customProps}
                entity={entity}
                onEdit={() => setOpenModal(section.id as EditModalType)}
                teamId={contextId ?? entity.id}
              />
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

const DrawerContent = ({
  entity,
  sectionGroups,
  contextId,
}: {
  entity: VisibilityEntity;
  sectionGroups: DrawerSectionGroup[];
  contextId?: string;
}) => {
  const { t, locale } = useLocale();
  const { me } = useAppData();
  const [openModal, setOpenModal] = useState<EditModalType | null>(null);

  const contextValue = useMemo(
    () => ({
      entity,
      setOpenModal,
      contextId,
    }),
    [entity, setOpenModal, contextId],
  );

  const isAgent = isAgentEntity(entity);
  const showAgentSlugRow = (me?.is_admin ?? false) && isAgent;
  const agentSlug = isAgent ? entity.agent_slug ?? null : null;
  const liveProfileUrl =
    showAgentSlugRow && me?.tenant_id
      ? getAgentLiveProfileUrl(me.tenant_id, locale, agentSlug, entity.id)
      : null;

  return (
    <DrawerContext.Provider value={contextValue}>
      <Box sx={{ p: 2 }}>
        <BannerSection entity={entity} teamId={contextId} />

        <Stack spacing={2} sx={{ mt: 2 }}>
          <Box>
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <PublishedStatus isPublished={!!entity.show_in_agency_pages} />
              <PublishMenu entity={entity} />
            </Stack>

            {showAgentSlugRow && (
              <Stack
                direction="row"
                sx={{ alignItems: 'center', gap: 0.5, mt: 1, minWidth: 0 }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', flexShrink: 0 }}
                >
                  {t('Profile URL')}:
                </Typography>
                {agentSlug && liveProfileUrl ? (
                  <MuiLink
                    href={liveProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {liveProfileUrl}
                  </MuiLink>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                      flex: 1,
                      fontStyle: 'italic',
                    }}
                  >
                    {t('Not set')}
                  </Typography>
                )}
                <Tooltip title={t('Edit profile URL')}>
                  <IconButton
                    size="small"
                    onClick={() => setOpenModal('agentSlug')}
                    sx={{ width: 24, height: 24 }}
                  >
                    <Pencil size={14} />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Box>

          {sectionGroups.map(group => (
            <SectionGroupCard
              key={group.id}
              group={group}
              entity={entity}
              contextId={contextId}
              setOpenModal={setOpenModal}
            />
          ))}
        </Stack>
      </Box>
      {isAgentEntity(entity) && (
        <AgentPhoneEditModal
          open={openModal === 'agentPhone'}
          onClose={() => setOpenModal(null)}
          entity={entity}
        />
      )}
      <UserFieldEditModal
        type={openModal !== 'agentPhone' ? openModal : null}
        open={!!openModal && openModal !== 'agentPhone'}
        onClose={() => setOpenModal(null)}
        entity={entity}
      />
    </DrawerContext.Provider>
  );
};

type VisibilityDrawerProps<TData, TEntity> = {
  onClose: () => void;
  entityId: string;
  teamId?: string;
  title: string;
  config: DrawerConfig<TData, TEntity>;
};

export const VisibilityDrawer = <TData, TEntity extends VisibilityEntity>({
  onClose,
  entityId,
  teamId,
  title,
  config,
}: VisibilityDrawerProps<TData, TEntity>) => {
  const { t } = useLocale();
  const { breakpoints } = useTheme();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const { data, loading, error } = useQuery<TData>(config.query, {
    variables: {
      [`${config.entityType}Id`]: entityId,
      ...(teamId && { teamId }),
    },
    skip: !entityId,
  });

  useEffect(() => {
    if (error) {
      setErrorLike(error);
    }
  }, [error]);

  const entity = data ? config.extractEntity(data) : null;

  return (
    <Drawer
      anchor="right"
      maxWidth={breakpoints.values.md}
      onClose={onClose}
      title={title}
    >
      {loading && <LoadingSpinner />}
      {apolloError && !loading && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error">
            {t('Failed to load profile data. Please try again.')}
          </Typography>
        </Box>
      )}
      {!loading && !apolloError && entity && (
        <DrawerContent
          entity={entity}
          sectionGroups={config.sectionGroups}
          contextId={teamId}
        />
      )}

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </Drawer>
  );
};
